import { PRIMARY_COLORS, TEXT_COLORS } from "@monthly/theme";
import HighchartsReact from "highcharts-react-official";
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

import { addWatermarkToHighchartsExport } from "../chartWatermark";
import Highcharts from "../HighChartsConfig";
import {
  formatNumberWithSeparators,
  truncateNumber,
} from "../utils/formatting";

interface LineChartProps {
  categories: string[];
  series: {
    name: string;
    data: number[];
    color?: string;
    dashStyle?: "Solid" | "Dash" | "Dot";
  }[];
  height?: number;
  showLegend?: boolean;
  yAxisFormat?: "percent" | "number";
  showDataLabels?: boolean;
  decimalPlaces?: number;
}

export interface LineChartRef {
  downloadImage: (filename?: string) => void;
}

export const LineChart = forwardRef<LineChartRef, LineChartProps>(
  (
    {
      categories,
      series,
      height = 250,
      showLegend = true,
      yAxisFormat = "number",
      showDataLabels = true,
      decimalPlaces = 2,
    },
    ref,
  ) => {
    const chartRef = useRef<HighchartsReact.RefObject>(null);

    useImperativeHandle(ref, () => ({
      downloadImage: (filename = "chart") => {
        if (chartRef.current?.chart) {
          addWatermarkToHighchartsExport(chartRef.current.chart, filename);
        }
      },
    }));

    const options: Highcharts.Options = {
      chart: {
        type: "line",
        height,
      },
      title: {
        text: undefined,
      },
      xAxis: {
        categories,
      },
      yAxis: {
        visible: false,
        title: {
          text: undefined,
        },
        labels: {
          enabled: false,
        },
        gridLineWidth: 0,
      },
      legend: {
        enabled: showLegend,
        align: "right",
        verticalAlign: "top",
        floating: true,
        y: -10,
      },
      plotOptions: {
        line: {
          marker: {
            enabled: true,
            radius: 4,
            fillColor: "#FFFFFF",
            lineWidth: 2,
            symbol: "circle",
            states: {
              hover: {
                enabled: true,
              },
            },
          },
          lineWidth: 2,
          dataLabels: {
            enabled: showDataLabels,
            formatter: function () {
              const value = truncateNumber(
                (this as { y?: number }).y ?? 0,
                decimalPlaces,
              );
              return yAxisFormat === "percent"
                ? `${formatNumberWithSeparators(value, decimalPlaces)}%`
                : formatNumberWithSeparators(value, decimalPlaces);
            },
            style: {
              fontFamily: '"Open Sans", sans-serif',
              fontSize: "10px",
              fontWeight: "500",
              color: TEXT_COLORS.secondary,
              textOutline: "none",
            },
            y: -8,
          },
        },
      },
      series: series.map((s, index) => ({
        type: "line" as const,
        name: s.name,
        data: s.data.map((value) => truncateNumber(value, decimalPlaces)),
        color:
          s.color || [PRIMARY_COLORS.main, "#22C55E", "#F59E0B"][index % 3],
        dashStyle: s.dashStyle || "Solid",
      })),
    };

    useEffect(() => {
      if (chartRef.current?.chart) {
        chartRef.current.chart.reflow();
      }
    }, []);

    return (
      <HighchartsReact
        ref={chartRef}
        highcharts={Highcharts}
        options={options}
      />
    );
  },
);

LineChart.displayName = "LineChart";
